import { useState, useMemo, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Copy, Mail, Search, Download, Newspaper, Send, Plus, Trash2, MailOpen, BarChart3, X, AlertTriangle, FileText, Image as ImageIcon, StickyNote, RotateCcw, Star, EyeOff, Award, ChevronDown } from "lucide-react";
import { api, apiErr, adminDelete } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CONTACTS = [
  // France
  { m: "Maddyness", c: "France", t: "Média startups / tech", e: "redaction@maddyness.com", w: "maddyness.com" },
  { m: "FrenchWeb", c: "France", t: "Média tech / business", e: "redaction@frenchweb.fr", w: "frenchweb.fr" },
  { m: "Numerama", c: "France", t: "Tech grand public", e: "redaction@numerama.com", w: "numerama.com" },
  { m: "Siècle Digital", c: "France", t: "Digital / innovation", e: "redaction@siecledigital.fr", w: "siecledigital.fr" },
  { m: "Presse-citron", c: "France", t: "Tech / mobile", e: "redaction@presse-citron.net", w: "presse-citron.net" },
  { m: "Frandroid", c: "France", t: "Mobile / applications", e: "redaction@frandroid.com", w: "frandroid.com" },
  { m: "01net", c: "France", t: "High-tech", e: "redaction@01net.com", w: "01net.com" },
  { m: "Journal du Net", c: "France", t: "Business / tech", e: "redaction@journaldunet.com", w: "journaldunet.com" },
  { m: "L'Usine Digitale", c: "France", t: "Transformation numérique", e: "redaction@usine-digitale.fr", w: "usine-digitale.fr" },
  { m: "La French Tech", c: "France", t: "Écosystème startups", e: "hello@lafrenchtech.com", w: "lafrenchtech.com" },
  // Suisse
  { m: "ICTjournal", c: "Suisse", t: "IT / numérique", e: "redaction@ictjournal.ch", w: "ictjournal.ch" },
  { m: "Startupticker", c: "Suisse", t: "Startups", e: "info@startupticker.ch", w: "startupticker.ch" },
  { m: "Netzwoche", c: "Suisse", t: "IT / digital", e: "redaktion@netzmedien.ch", w: "netzwoche.ch" },
  { m: "Bilan", c: "Suisse", t: "Économie / tech", e: "redaction@bilan.ch", w: "bilan.ch" },
  { m: "Le Temps", c: "Suisse", t: "Actualité / tech", e: "redaction@letemps.ch", w: "letemps.ch" },
  { m: "Watson", c: "Suisse", t: "Actualité / digital", e: "redaction@watson.ch", w: "watson.ch" },
  { m: "PME Magazine", c: "Suisse", t: "Entrepreneuriat", e: "redaction@pme.ch", w: "pme.ch" },
  // Belgique
  { m: "Data News", c: "Belgique", t: "IT / tech", e: "redactie@datanews.be", w: "datanews.knack.be" },
  { m: "Trends-Tendances", c: "Belgique", t: "Business / tech", e: "redaction@trends.be", w: "trends.levif.be" },
  { m: "L'Echo", c: "Belgique", t: "Économie / tech", e: "redaction@lecho.be", w: "lecho.be" },
  { m: "Bruzz", c: "Belgique", t: "Média régional / tech", e: "info@bruzz.be", w: "bruzz.be" },
  { m: "Startups.be", c: "Belgique", t: "Écosystème startups", e: "info@startups.be", w: "startups.be" },
  // Luxembourg
  { m: "Silicon Luxembourg", c: "Luxembourg", t: "Startups / tech", e: "hello@siliconluxembourg.lu", w: "siliconluxembourg.lu" },
  { m: "Paperjam", c: "Luxembourg", t: "Business / tech", e: "press@paperjam.lu", w: "paperjam.lu" },
  { m: "Delano", c: "Luxembourg", t: "Actualité (EN)", e: "info@delano.lu", w: "delano.lu" },
  { m: "Luxembourg Times", c: "Luxembourg", t: "Actualité (EN)", e: "news@luxtimes.lu", w: "luxtimes.lu" },
  { m: "Lëtzebuerger Journal", c: "Luxembourg", t: "Actualité", e: "info@journal.lu", w: "journal.lu" },
  // International
  { m: "TechCrunch", c: "États-Unis", t: "Tech / startups", e: "tips@techcrunch.com", w: "techcrunch.com" },
  { m: "The Verge", c: "États-Unis", t: "Tech grand public", e: "tips@theverge.com", w: "theverge.com" },
  { m: "Wired", c: "États-Unis", t: "Tech / innovation", e: "mail@wired.com", w: "wired.com" },
  { m: "VentureBeat", c: "États-Unis", t: "Tech / business", e: "editors@venturebeat.com", w: "venturebeat.com" },
  { m: "Mashable", c: "États-Unis", t: "Tech / culture", e: "news@mashable.com", w: "mashable.com" },
  { m: "Engadget", c: "États-Unis", t: "Gadgets / mobile", e: "tips@engadget.com", w: "engadget.com" },
  { m: "TechRadar", c: "Royaume-Uni", t: "Tech / mobile", e: "news@techradar.com", w: "techradar.com" },
  { m: "Sifted", c: "Royaume-Uni", t: "Startups (Europe)", e: "hello@sifted.eu", w: "sifted.eu" },
  { m: "The Next Web", c: "Pays-Bas", t: "Tech / startups", e: "tips@thenextweb.com", w: "thenextweb.com" },
  { m: "Tech.eu", c: "Europe", t: "Startups / tech", e: "editorial@tech.eu", w: "tech.eu" },
  { m: "Gründerszene", c: "Allemagne", t: "Startups", e: "redaktion@businessinsider.de", w: "businessinsider.de/gruenderszene" },
  { m: "El Referente", c: "Espagne", t: "Startups", e: "info@elreferente.es", w: "elreferente.es" },
  { m: "Fast Company", c: "États-Unis", t: "Innovation / business", e: "tips@fastcompany.com", w: "fastcompany.com" },
  { m: "CNET", c: "États-Unis", t: "Tech grand public", e: "news@cnet.com", w: "cnet.com" },
  { m: "Gizmodo", c: "États-Unis", t: "Tech / culture", e: "tips@gizmodo.com", w: "gizmodo.com" },
  // Canada
  { m: "BetaKit", c: "Canada", t: "Startups / tech", e: "editor@betakit.com", w: "betakit.com" },
  { m: "MobileSyrup", c: "Canada", t: "Mobile / applications", e: "tips@mobilesyrup.com", w: "mobilesyrup.com" },
  { m: "IT World Canada", c: "Canada", t: "IT / numérique", e: "editorial@itwc.ca", w: "itworldcanada.com" },
  { m: "Les Affaires", c: "Canada", t: "Business / tech (Québec)", e: "redaction@lesaffaires.com", w: "lesaffaires.com" },
  { m: "La Presse", c: "Canada", t: "Actualité / tech (Québec)", e: "nouvelles@lapresse.ca", w: "lapresse.ca" },
  // Afrique
  { m: "TechCabal", c: "Nigeria", t: "Startups / tech", e: "hello@techcabal.com", w: "techcabal.com" },
  { m: "Techpoint Africa", c: "Nigeria", t: "Tech / startups", e: "hello@techpoint.africa", w: "techpoint.africa" },
  { m: "Disrupt Africa", c: "Afrique", t: "Startups", e: "info@disrupt-africa.com", w: "disrupt-africa.com" },
  { m: "Ventureburn", c: "Afrique du Sud", t: "Startups / tech", e: "editor@ventureburn.com", w: "ventureburn.com" },
  { m: "Agence Ecofin", c: "Afrique", t: "Économie / tech (FR)", e: "contact@agenceecofin.com", w: "agenceecofin.com" },
  { m: "Médias24", c: "Maroc", t: "Économie / tech (FR)", e: "redaction@medias24.com", w: "medias24.com" },
  { m: "WeAreTech Africa", c: "Afrique", t: "Tech / numérique (FR)", e: "contact@wearetech.africa", w: "wearetech.africa" },
  // Asie
  { m: "Tech in Asia", c: "Singapour", t: "Startups / tech", e: "editorial@techinasia.com", w: "techinasia.com" },
  { m: "e27", c: "Singapour", t: "Startups", e: "writers@e27.co", w: "e27.co" },
  { m: "KrASIA", c: "Asie", t: "Tech / business", e: "editor@kr-asia.com", w: "kr-asia.com" },
  { m: "YourStory", c: "Inde", t: "Startups", e: "press@yourstory.com", w: "yourstory.com" },
  { m: "Inc42", c: "Inde", t: "Startups / tech", e: "editorial@inc42.com", w: "inc42.com" },
  { m: "Nikkei Asia", c: "Japon", t: "Économie / tech", e: "asia.editor@nex.nikkei.com", w: "asia.nikkei.com" },
  { m: "TechNode", c: "Chine", t: "Tech / startups", e: "editor@technode.com", w: "technode.com" },
  // Moyen-Orient
  { m: "Wamda", c: "Moyen-Orient", t: "Startups (MENA)", e: "info@wamda.com", w: "wamda.com" },
  { m: "MENAbytes", c: "Moyen-Orient", t: "Tech / startups (MENA)", e: "info@menabytes.com", w: "menabytes.com" },
  { m: "Magnitt", c: "Émirats arabes unis", t: "Startups / venture", e: "info@magnitt.com", w: "magnitt.com" },
  { m: "Arabian Business", c: "Émirats arabes unis", t: "Business / tech", e: "editor@arabianbusiness.com", w: "arabianbusiness.com" },
  // Océanie
  { m: "Startup Daily", c: "Australie", t: "Startups", e: "editor@startupdaily.net", w: "startupdaily.net" },
  { m: "SmartCompany", c: "Australie", t: "Business / tech", e: "news@smartcompany.com.au", w: "smartcompany.com.au" },
  // Amérique latine
  { m: "Contxto", c: "Amérique latine", t: "Startups (LatAm)", e: "editorial@contxto.com", w: "contxto.com" },
  { m: "LABS", c: "Brésil", t: "Tech / business", e: "contato@labsnews.com", w: "labsnews.com" },
  // Presse people (France)
  { m: "Voici", c: "People", t: "Presse people", e: "redaction@voici.fr", w: "voici.fr" },
  { m: "Gala", c: "People", t: "Presse people", e: "redaction@gala.fr", w: "gala.fr" },
  { m: "Public", c: "People", t: "Presse people", e: "redaction@public.fr", w: "public.fr" },
  { m: "Closer", c: "People", t: "Presse people", e: "redaction@closermag.fr", w: "closermag.fr" },
  { m: "Paris Match", c: "People", t: "Actualité / people", e: "redaction@parismatch.com", w: "parismatch.com" },
  { m: "Point de Vue", c: "People", t: "People / royauté", e: "redaction@pointdevue.fr", w: "pointdevue.fr" },
  { m: "Purepeople", c: "People", t: "People / web", e: "redaction@purepeople.com", w: "purepeople.com" },
  { m: "Non Stop People", c: "People", t: "People / TV", e: "contact@nonstoppeople.fr", w: "nonstoppeople.com" },
  { m: "Oops", c: "People", t: "Presse people", e: "redaction@oops.fr", w: "oops.fr" },
  // Magazines grand public (France)
  { m: "Elle", c: "Magazine", t: "Féminin / lifestyle", e: "redaction@elle.fr", w: "elle.fr" },
  { m: "Marie Claire", c: "Magazine", t: "Féminin", e: "redaction@marieclaire.fr", w: "marieclaire.fr" },
  { m: "Grazia", c: "Magazine", t: "Féminin / mode", e: "redaction@grazia.fr", w: "grazia.fr" },
  { m: "Femme Actuelle", c: "Magazine", t: "Féminin", e: "redaction@femmeactuelle.fr", w: "femmeactuelle.fr" },
  { m: "Vogue France", c: "Magazine", t: "Mode / luxe", e: "redaction@vogue.fr", w: "vogue.fr" },
  { m: "GQ France", c: "Magazine", t: "Masculin / lifestyle", e: "redaction@gqmagazine.fr", w: "gqmagazine.fr" },
  { m: "Vanity Fair France", c: "Magazine", t: "Culture / people", e: "redaction@vanityfair.fr", w: "vanityfair.fr" },
  { m: "Télérama", c: "Magazine", t: "Culture / médias", e: "redaction@telerama.fr", w: "telerama.fr" },
  { m: "L'Express", c: "Magazine", t: "Actualité / news", e: "redaction@lexpress.fr", w: "lexpress.fr" },
  { m: "Le Point", c: "Magazine", t: "Actualité / news", e: "redaction@lepoint.fr", w: "lepoint.fr" },
  { m: "L'Obs", c: "Magazine", t: "Actualité / société", e: "redaction@nouvelobs.com", w: "nouvelobs.com" },
  { m: "Marianne", c: "Magazine", t: "Actualité / société", e: "redaction@marianne.net", w: "marianne.net" },
  { m: "Le Figaro Magazine", c: "Magazine", t: "Actualité / société", e: "redaction@lefigaro.fr", w: "lefigaro.fr" },
  { m: "Femme Actuelle Senior", c: "Magazine", t: "Lifestyle", e: "redaction@prismamedia.com", w: "prismamedia.com" },
  // Sport & TV (France)
  { m: "L'Équipe", c: "Sport & TV", t: "Sport / football", e: "redaction@lequipe.fr", w: "lequipe.fr" },
  { m: "France Football", c: "Sport & TV", t: "Football", e: "redaction@francefootball.fr", w: "francefootball.fr" },
  { m: "So Foot", c: "Sport & TV", t: "Football / culture", e: "redaction@sofoot.com", w: "sofoot.com" },
  { m: "Onze Mondial", c: "Sport & TV", t: "Football", e: "redaction@onzemondial.com", w: "onzemondial.com" },
  { m: "Télé Loisirs", c: "Sport & TV", t: "TV / médias", e: "redaction@teleloisirs.fr", w: "programme-tv.net" },
  { m: "Télé 7 Jours", c: "Sport & TV", t: "TV / médias", e: "redaction@tele7jours.fr", w: "programme.tv" },
  { m: "Télé Star", c: "Sport & TV", t: "TV / people", e: "redaction@telestar.fr", w: "telestar.fr" },
  // Presse Suisse (généraliste / people / magazines / TV)
  { m: "L'illustré", c: "Suisse", t: "People / magazine", e: "redaction@illustre.ch", w: "illustre.ch" },
  { m: "Femina", c: "Suisse", t: "Féminin / lifestyle", e: "redaction@femina.ch", w: "femina.ch" },
  { m: "Le Matin", c: "Suisse", t: "Actualité / grand public", e: "redaction@lematin.ch", w: "lematin.ch" },
  { m: "20 minutes (CH)", c: "Suisse", t: "Actualité / grand public", e: "redaction@20minutes.ch", w: "20min.ch" },
  { m: "Tribune de Genève", c: "Suisse", t: "Actualité", e: "redaction@tdg.ch", w: "tdg.ch" },
  { m: "24 heures", c: "Suisse", t: "Actualité", e: "redaction@24heures.ch", w: "24heures.ch" },
  { m: "Schweizer Illustrierte", c: "Suisse", t: "People (DE)", e: "redaktion@schweizer-illustrierte.ch", w: "schweizer-illustrierte.ch" },
  { m: "Blick", c: "Suisse", t: "Actualité (DE)", e: "redaktion@blick.ch", w: "blick.ch" },
  { m: "RTS", c: "Suisse", t: "Radio / TV", e: "info@rts.ch", w: "rts.ch" },
  // Presse Belgique (généraliste / people / magazines / TV)
  { m: "Le Soir", c: "Belgique", t: "Actualité", e: "redaction@lesoir.be", w: "lesoir.be" },
  { m: "La Libre Belgique", c: "Belgique", t: "Actualité", e: "redaction@lalibre.be", w: "lalibre.be" },
  { m: "DH Les Sports+", c: "Belgique", t: "Actualité / sport", e: "redaction@dhnet.be", w: "dhnet.be" },
  { m: "Ciné Télé Revue", c: "Belgique", t: "People / TV", e: "redaction@cinetelerevue.be", w: "cinetelerevue.be" },
  { m: "Paris Match Belgique", c: "Belgique", t: "People / actualité", e: "redaction@parismatch.be", w: "parismatch.be" },
  { m: "Moustique", c: "Belgique", t: "Magazine / culture", e: "redaction@moustique.be", w: "moustique.lalibre.be" },
  { m: "7sur7", c: "Belgique", t: "Actualité / web", e: "redaction@7sur7.be", w: "7sur7.be" },
  { m: "RTBF", c: "Belgique", t: "Radio / TV", e: "info@rtbf.be", w: "rtbf.be" },
  { m: "Sudinfo", c: "Belgique", t: "Actualité", e: "redaction@sudinfo.be", w: "sudinfo.be" },
  // Presse Luxembourg (généraliste / magazines / TV)
  { m: "RTL Luxembourg", c: "Luxembourg", t: "Radio / TV", e: "info@rtl.lu", w: "rtl.lu" },
  { m: "Luxemburger Wort", c: "Luxembourg", t: "Actualité (DE/FR)", e: "redaktion@wort.lu", w: "wort.lu" },
  { m: "Le Quotidien", c: "Luxembourg", t: "Actualité (FR)", e: "redaction@lequotidien.lu", w: "lequotidien.lu" },
  { m: "Tageblatt", c: "Luxembourg", t: "Actualité (DE)", e: "redaktion@tageblatt.lu", w: "tageblatt.lu" },
  { m: "L'essentiel", c: "Luxembourg", t: "Actualité / grand public", e: "redaction@lessentiel.lu", w: "lessentiel.lu" },
  { m: "Revue", c: "Luxembourg", t: "Magazine", e: "redaktioun@revue.lu", w: "revue.lu" },
  // ===== Lancement — Relations presse (FR) : cibles prioritaires (★ = priorité) =====
  // Presse startup / tech / innovation
  { m: "Les Échos", c: "Lancement FR", t: "Économie · startup", e: "redaction@lesechos.fr", w: "lesechos.fr", p: 5 },
  { m: "Les Échos Start", c: "Lancement FR", t: "Entrepreneuriat / jeunes", e: "start@lesechos.fr", w: "start.lesechos.fr", p: 5 },
  { m: "BFM Business", c: "Lancement FR", t: "Économie / business TV", e: "redaction@bfmbusiness.fr", w: "bfmbusiness.com", p: 4 },
  { m: "Tech&Co (BFMTV)", c: "Lancement FR", t: "Tech grand public", e: "techandco@bfmtv.com", w: "bfmtv.com/tech", p: 4 },
  { m: "Forbes France", c: "Lancement FR", t: "Business / innovation", e: "redaction@forbes.fr", w: "forbes.fr", p: 4 },
  { m: "La Tribune", c: "Lancement FR", t: "Économie / innovation", e: "redaction@latribune.fr", w: "latribune.fr", p: 4 },
  { m: "Capital", c: "Lancement FR", t: "Économie grand public", e: "redaction@capital.fr", w: "capital.fr", p: 3 },
  { m: "Challenges", c: "Lancement FR", t: "Économie / business", e: "redaction@challenges.fr", w: "challenges.fr", p: 3 },
  { m: "Usbek & Rica", c: "Lancement FR", t: "Innovation / prospective", e: "redaction@usbeketrica.com", w: "usbeketrica.com", p: 3 },
  { m: "Le Figaro Économie", c: "Lancement FR", t: "Économie / tech", e: "economie@lefigaro.fr", w: "lefigaro.fr/economie", p: 4 },
  { m: "AFP", c: "Lancement FR", t: "Agence de presse", e: "redaction@afp.com", w: "afp.com", p: 3 },
  // Presse grand public / audiovisuel
  { m: "Le Parisien", c: "Lancement FR", t: "Grand public / national", e: "actualites@leparisien.fr", w: "leparisien.fr", p: 4 },
  { m: "20 Minutes", c: "Lancement FR", t: "Grand public / national", e: "web@20minutes.fr", w: "20minutes.fr", p: 4 },
  { m: "BFMTV", c: "Lancement FR", t: "Actualité / TV", e: "redaction@bfmtv.com", w: "bfmtv.com", p: 4 },
  { m: "France 3", c: "Lancement FR", t: "TV / régions", e: "contact@france3.fr", w: "france3.fr", p: 4 },
  { m: "France Bleu", c: "Lancement FR", t: "Radio / régions", e: "contact@francebleu.fr", w: "francebleu.fr", p: 4 },
  { m: "franceinfo", c: "Lancement FR", t: "Actualité / radio-TV", e: "redaction@franceinfo.fr", w: "franceinfo.fr", p: 3 },
  { m: "RTL", c: "Lancement FR", t: "Radio nationale", e: "redaction@rtl.fr", w: "rtl.fr", p: 3 },
  { m: "Europe 1", c: "Lancement FR", t: "Radio nationale", e: "redaction@europe1.fr", w: "europe1.fr", p: 3 },
  { m: "RMC", c: "Lancement FR", t: "Radio / actualité", e: "redaction@rmc.fr", w: "rmc.bfmtv.com", p: 3 },
  { m: "CNews", c: "Lancement FR", t: "Actualité / TV", e: "redaction@cnews.fr", w: "cnews.fr", p: 3 },
  { m: "Konbini", c: "Lancement FR", t: "Web / jeunes", e: "redaction@konbini.com", w: "konbini.com", p: 3 },
  { m: "Brut", c: "Lancement FR", t: "Web / vidéo", e: "contact@brut.media", w: "brut.media", p: 3 },
  // Presse régionale (relais lancement)
  { m: "Ouest-France", c: "Lancement FR", t: "Régional / national", e: "redaction@ouest-france.fr", w: "ouest-france.fr", p: 4 },
  { m: "Sud Ouest", c: "Lancement FR", t: "Régional", e: "redaction@sudouest.fr", w: "sudouest.fr", p: 3 },
  { m: "La Voix du Nord", c: "Lancement FR", t: "Régional", e: "redaction@lavoixdunord.fr", w: "lavoixdunord.fr", p: 3 },
  { m: "Le Télégramme", c: "Lancement FR", t: "Régional (Bretagne)", e: "redaction@letelegramme.fr", w: "letelegramme.fr", p: 3 },
  { m: "Le Dauphiné Libéré", c: "Lancement FR", t: "Régional", e: "redaction@ledauphine.com", w: "ledauphine.com", p: 3 },
  { m: "DNA (Alsace)", c: "Lancement FR", t: "Régional", e: "redaction@dna.fr", w: "dna.fr", p: 3 },
  { m: "Nice-Matin", c: "Lancement FR", t: "Régional", e: "redaction@nicematin.fr", w: "nicematin.fr", p: 3 },
  { m: "La Provence", c: "Lancement FR", t: "Régional", e: "redaction@laprovence.com", w: "laprovence.com", p: 3 },
  { m: "La Dépêche du Midi", c: "Lancement FR", t: "Régional", e: "redaction@ladepeche.fr", w: "ladepeche.fr", p: 3 },
  { m: "Le Progrès", c: "Lancement FR", t: "Régional (Lyon)", e: "redaction@leprogres.fr", w: "leprogres.fr", p: 3 },
  // Podcasts / web TV entrepreneurs
  { m: "Génération Do It Yourself", c: "Lancement FR", t: "Podcast entrepreneurs", e: "contact@gdiy.fr", w: "gdiy.fr", p: 4 },
  { m: "Le Gratin (Pauline Laigneau)", c: "Lancement FR", t: "Podcast entrepreneurs", e: "contact@paulinelaigneau.com", w: "paulinelaigneau.com", p: 3 },
  { m: "Widoobiz", c: "Lancement FR", t: "Média / podcast entrepreneurs", e: "contact@widoobiz.com", w: "widoobiz.com", p: 3 },
  // Presse spécialisée développeurs
  { m: "Developpez.com", c: "Lancement FR", t: "Développeurs / IT", e: "redaction@developpez.com", w: "developpez.com", p: 2 },
];

const REGIONS = {
  "Lancement — Relations presse (FR)": ["Lancement FR"],
  "Europe francophone": ["France", "Suisse", "Belgique", "Luxembourg"],
  "Amérique du Nord": ["États-Unis", "Canada"],
  "Europe": ["Royaume-Uni", "Pays-Bas", "Europe", "Allemagne", "Espagne"],
  "Afrique": ["Nigeria", "Afrique", "Afrique du Sud", "Maroc"],
  "Asie": ["Singapour", "Asie", "Inde", "Japon", "Chine"],
  "Moyen-Orient": ["Moyen-Orient", "Émirats arabes unis"],
  "Océanie": ["Australie"],
  "Amérique latine": ["Amérique latine", "Brésil"],
  "Presse people & magazines (FR)": ["People", "Magazine", "Sport & TV"],
};
const REGION_KEYS = ["Tous", ...Object.keys(REGIONS)];
const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const EMAIL_SUBJECT = "Rentrée 2027 – APP CHALLENGE ACADEMY, une académie française ouverte au monde entier";

const EMAIL_BODY_LONG = `Bonjour [MEDIA],

Je me permets de vous présenter, à l'occasion de cette rentrée 2027, APP CHALLENGE ACADEMY (appchallengeacademy.com), une académie française à vocation internationale, dédiée à la création et au développement d'applications.

Vous avez une idée d'application ?
Nous pouvons la transformer en réalité.

Le concept est simple : quel que soit le pays où vous vivez, vous pouvez proposer gratuitement votre idée d'application mobile ou web.

Chaque semaine, notre académie étudie les idées proposées et sélectionne une idée gagnante. Son créateur bénéficie alors du développement gratuit de son application, de l'idée jusqu'à sa réalisation.

Une académie française, une ambition mondiale
Née en France, APP CHALLENGE ACADEMY a été pensée dès le départ pour s'adresser au monde entier.

La plateforme est disponible en 13 langues, permettant à des créateurs de différents pays de proposer leur idée, de participer au challenge et de suivre les projets sélectionnés.

Une bonne idée peut venir de partout dans le monde.

Pourquoi ce concept peut intéresser vos lecteurs
• Accessible à tous : aucune compétence en programmation et aucun budget ne sont nécessaires pour participer.
• Un challenge chaque semaine : une idée est sélectionnée et son créateur voit son projet devenir une véritable application.
• Une dimension internationale : des participants du monde entier peuvent proposer leurs idées grâce à une plateforme multilingue.
• Une histoire humaine derrière chaque application : une personne, une idée, un challenge et une application qui voit réellement le jour.
• Une nouvelle façon de faire émerger les idées : nous voulons donner une chance à des concepts qui n'auraient peut-être jamais eu l'occasion d'être développés.

Notre ambition : devenir une véritable académie mondiale des idées d'applications, née en France et ouverte au monde entier.

À l'occasion de son lancement, nous pouvons vous proposer :
• un accès en avant-première à la plateforme ;
• une présentation du fonctionnement du challenge ;
• une interview des fondateurs ;
• le portrait du premier créateur sélectionné et de son application ;
• des visuels et un kit presse sur demande.

Si le concept retient votre attention, je serais ravi de vous présenter APP CHALLENGE ACADEMY et son ambition internationale pour cette rentrée 2027.

Bien cordialement,

Service Marketing
APP CHALLENGE ACADEMY
Académie française des défis d'applications

contact@appchallengeacademy.com
appchallengeacademy.com`;

const EMAIL_BODY_SHORT = `Bonjour [MEDIA],

À l'occasion de la rentrée 2027, je souhaite vous présenter APP CHALLENGE ACADEMY (appchallengeacademy.com), une académie française à vocation internationale dédiée à la création d'applications.

Le concept : partout dans le monde, chacun peut proposer gratuitement son idée d'application mobile ou web. Chaque semaine, une idée est sélectionnée et son créateur bénéficie du développement gratuit de son application. La plateforme est disponible en 13 langues.

Nous pouvons vous proposer un accès en avant-première, une interview des fondateurs et un kit presse.

Seriez-vous intéressé(e) pour en parler ?

Bien cordialement,
Service Marketing — APP CHALLENGE ACADEMY
contact@appchallengeacademy.com
appchallengeacademy.com`;

const TEMPLATES = {
  long: { label: "Complet", subject: EMAIL_SUBJECT, body: EMAIL_BODY_LONG },
  court: { label: "Court", subject: EMAIL_SUBJECT, body: EMAIL_BODY_SHORT },
};
const personalize = (text, media) => text.replace(/\[MEDIA\]/g, media || "chère rédaction");

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const download = (name, content, type = "text/plain") => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

export default function AdminPress() {
  const { tf: t } = useLang();
  const regLabel = (c) => { const k = `reg_${slug(c)}`; const v = t(k); return v === k ? c : v; };
  const [country, setCountry] = useState("Tous");
  const [search, setSearch] = useState("");
  const [custom, setCustom] = useState([]);
  const [sends, setSends] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [nc, setNc] = useState({ m: "", c: "France", t: "", e: "" });
  const [confirm, setConfirm] = useState(null); // {label, recipients, scope, step}
  const [sending, setSending] = useState(false);
  const [tpl, setTpl] = useState("long");
  const [custTpls, setCustTpls] = useState([]);
  const [builtinTpls, setBuiltinTpls] = useState({});
  const [tplModal, setTplModal] = useState(null); // { mode:'add'|'edit', id?, label, subject, body }
  const [savingTpl, setSavingTpl] = useState(false);
  const [hidden, setHidden] = useState([]);
  const [notes, setNotes] = useState({});
  const [noteModal, setNoteModal] = useState(null); // { x, particularites, note }
  const [showHidden, setShowHidden] = useState(false);
  const [importing, setImporting] = useState(false);
  // Onglets des actions presse (tous fermés par défaut ; clic = ouvrir, reclic = fermer).
  const PRESS_TABS = [
    { key: "contacts", label: "Contacts", icon: Mail, color: "cyan" },
    { key: "campagne", label: "Campagne e-mail", icon: Send, color: "green" },
    { key: "statistiques", label: "Statistiques", icon: BarChart3, color: "amber" },
  ];
  const PRESS_TAB_COLORS = {
    cyan: { active: "border-cyan bg-cyan/15 text-cyan", idle: "border-cyan/30 bg-cyan/5 text-cyan/80 hover:border-cyan hover:text-cyan" },
    green: { active: "border-green-400 bg-green-400/15 text-green-400", idle: "border-green-400/30 bg-green-400/5 text-green-400/80 hover:border-green-400 hover:text-green-400" },
    amber: { active: "border-amber-400 bg-amber-400/15 text-amber-300", idle: "border-amber-400/30 bg-amber-400/5 text-amber-300/80 hover:border-amber-400 hover:text-amber-300" },
  };
  const allTemplates = useMemo(() => {
    const base = { ...TEMPLATES };
    ["long", "court"].forEach((k) => {
      if (builtinTpls[k]?.subject) base[k] = { ...base[k], subject: builtinTpls[k].subject, body: builtinTpls[k].body };
    });
    (custTpls || []).forEach((tp) => { base[tp.id] = { label: tp.label, subject: tp.subject, body: tp.body, custom: true, id: tp.id }; });
    return base;
  }, [custTpls, builtinTpls]);
  const active = allTemplates[tpl] || TEMPLATES.long;
  const tplLabel = (key) => (allTemplates[key]?.custom ? allTemplates[key].label : t(`prs_tpl_${key}`));

  const loadContacts = useCallback(async () => {
    try {
      const [c, h, n, tp, bt] = await Promise.all([
        api.get("/admin/press/contacts"),
        api.get("/admin/press/hidden"),
        api.get("/admin/press/notes"),
        api.get("/admin/press/email-templates"),
        api.get("/admin/press/email-templates/builtin"),
      ]);
      setCustom(c.data); setHidden(h.data || []); setNotes(n.data || {}); setCustTpls(tp.data || []); setBuiltinTpls(bt.data || {});
    } catch (e) { /* ignore */ }
  }, []);
  const loadSends = useCallback(async () => {
    try {
      const [s, a, tr] = await Promise.all([
        api.get("/admin/press/sends"),
        api.get("/admin/press/analytics"),
        api.get("/admin/press/tracking"),
      ]);
      setSends(s.data); setAnalytics(a.data); setTracking(tr.data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadContacts(); loadSends(); }, [loadContacts, loadSends]);

  // Map email -> dernier envoi (sends triés desc par date côté backend)
  const sendMap = useMemo(() => {
    const m = {};
    for (const s of sends) { if (!m[s.email]) m[s.email] = s; }
    return m;
  }, [sends]);

  const addCustom = async () => {
    if (!nc.m || !nc.e) return toast.error(t("prs_toast_required"));
    try {
      const { data } = await api.post("/admin/press/contacts", { m: nc.m, c: nc.c || "France", t: nc.t, e: nc.e, w: "" });
      setNc({ m: "", c: "France", t: "", e: "" });
      if (data?.ambassador_created) toast.success(`Média ajouté et intégré automatiquement aux Ambassadeurs (un code a été créé).`, { duration: 6000 });
      else toast.success(t("prs_toast_added"));
      loadContacts();
    } catch (e) { toast.error(apiErr(e, t("prs_toast_add_fail"))); }
  };
  const removeCustom = async (id) => {
    try { if (!await adminDelete(`/admin/press/contacts/${id}`)) return; toast.success(t("prs_toast_deleted")); loadContacts(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const hideContacts = async (emails) => {
    try { await api.post("/admin/press/hide", { emails }); loadContacts(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteRow = (x) => {
    if (x.custom) { if (window.confirm(`Supprimer définitivement le contact « ${x.m} » ?`)) removeCustom(x.id); }
    else { if (window.confirm(`Masquer le contact « ${x.m} » ? Il ne sera plus affiché ni contacté.`)) hideContacts([x.e]); }
  };
  const disableRegion = () => {
    const emails = list.map((x) => x.e);
    if (!emails.length) return;
    const zone = country === "Tous" ? "la liste affichée" : `la région « ${regLabel(country)} »`;
    if (window.confirm(`Désactiver ${zone} (${emails.length} contact(s)) ? Ces contacts ne seront plus affichés ni contactés. Vous pourrez les réafficher.`)) {
      hideContacts(emails).then(() => toast.success(`${emails.length} contact(s) désactivé(s).`));
    }
  };
  const restoreAll = async () => {
    try { if (!await adminDelete("/admin/press/hide")) return; toast.success("Contacts réaffichés."); loadContacts(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const unhideContacts = async (emails, label) => {
    try {
      await api.post("/admin/press/unhide", { emails });
      toast.success(label || `${emails.length} contact(s) réactivé(s).`);
      loadContacts();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveNote = async () => {
    if (!noteModal) return;
    try {
      await api.put("/admin/press/notes", { email: noteModal.x.e, particularites: noteModal.particularites || "", note: noteModal.note || "" });
      toast.success("Fiche mise à jour.");
      setNotes({ ...notes, [noteModal.x.e.toLowerCase()]: { particularites: noteModal.particularites || "", note: noteModal.note || "" } });
      setNoteModal(null);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const getNote = (email) => notes[(email || "").toLowerCase()] || notes[email] || null;
  const trk = (email) => (tracking?.by_media || {})[(email || "").toLowerCase()] || null;  const hasNote = (email) => { const n = getNote(email); return !!(n && (n.particularites || n.note)); };

  const saveTemplate = async () => {
    if (!tplModal) return;
    const { label, subject, body } = tplModal;
    if (tplModal.builtin) {
      if (!subject?.trim() || !body?.trim()) return toast.error("Objet et message sont obligatoires.");
      setSavingTpl(true);
      try {
        const { data } = await api.put(`/admin/press/email-templates/builtin/${tplModal.builtin}`, { subject, body });
        setBuiltinTpls((s) => ({ ...s, [tplModal.builtin]: { subject: data.subject, body: data.body } }));
        toast.success("Modèle enregistré.");
        setTplModal(null);
      } catch (e) { toast.error(apiErr(e)); }
      setSavingTpl(false);
      return;
    }
    if (!label?.trim() || !subject?.trim() || !body?.trim()) return toast.error("Intitulé, objet et message sont obligatoires.");
    setSavingTpl(true);
    try {
      if (tplModal.mode === "edit") {
        const { data } = await api.put(`/admin/press/email-templates/${tplModal.id}`, { label, subject, body });
        setCustTpls((s) => s.map((x) => (x.id === data.id ? data : x)));
        toast.success("Modèle mis à jour.");
      } else {
        const { data } = await api.post("/admin/press/email-templates", { label, subject, body });
        setCustTpls((s) => [...s, data]); setTpl(data.id);
        toast.success("Modèle ajouté.");
      }
      setTplModal(null);
    } catch (e) { toast.error(apiErr(e)); }
    setSavingTpl(false);
  };
  const resetBuiltin = async (key) => {
    if (!window.confirm("Réinitialiser ce modèle à son texte d'origine ?")) return;
    try {
      if (!await adminDelete(`/admin/press/email-templates/builtin/${key}`)) return;
      setBuiltinTpls((s) => { const n = { ...s }; delete n[key]; return n; });
      toast.success("Modèle réinitialisé.");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm("Supprimer définitivement ce modèle d'e-mail ?")) return;
    try {
      if (!await adminDelete(`/admin/press/email-templates/${id}`)) return;
      setCustTpls((s) => s.filter((x) => x.id !== id));
      if (tpl === id) setTpl("long");
      toast.success("Modèle supprimé.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const pressToAmbassadors = async () => {
    const all = [...custom, ...CONTACTS];
    const contacts = all.filter((x) => x.e).map((x) => ({ name: x.m, email: x.e, note: `Presse · ${x.c}` }));
    if (!window.confirm(`Créer un code ambassadeur unique pour ${contacts.length} acteur(s) presse ? Les acteurs déjà dotés d'un code seront ignorés.`)) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/ambassadors/press-import", { contacts });
      toast.success(`${data.created} code(s) créé(s) · ${data.skipped} ignoré(s). Voir l'onglet Ambassadeurs.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setImporting(false); }
  };

  const hiddenSet = useMemo(() => new Set((hidden || []).map((e) => e.toLowerCase())), [hidden]);
  const ALL = useMemo(() => [
    ...custom.map((x) => ({ ...x, custom: true })),
    ...CONTACTS,
  ].filter((x) => !hiddenSet.has((x.e || "").toLowerCase())), [custom, hiddenSet]);

  // Contacts désactivés (pour l'espace « Désactivés » et la réactivation)
  const hiddenList = useMemo(() => [
    ...custom.map((x) => ({ ...x, custom: true })),
    ...CONTACTS,
  ].filter((x) => hiddenSet.has((x.e || "").toLowerCase())), [custom, hiddenSet]);

  const hiddenByCountry = useMemo(() => {
    const g = {};
    for (const x of hiddenList) { (g[x.c] = g[x.c] || []).push(x); }
    return g;
  }, [hiddenList]);

  const list = useMemo(() => ALL.filter((x) => {
    const okC = country === "Tous" || (REGIONS[country] || []).includes(x.c);
    const s = search.toLowerCase();
    const okS = !s || (x.m + x.c + x.t + x.e + (x.w || "")).toLowerCase().includes(s);
    return okC && okS;
  }), [ALL, country, search]);

  // ---- Envoi serveur réel (SMTP) ----
  const doSend = async (recipients, scope) => {
    if (!recipients.length) return toast.error(t("prs_toast_no_recipients"));
    setSending(true);
    try {
      const r = await api.post("/admin/press/send", {
        recipients, subject: active.subject, body: active.body, scope, base_url: BACKEND,
      });
      const { total, sent, smtp_configured } = r.data;
      if (!smtp_configured) {
        toast.warning(`${t("prs_toast_smtp_a")} ${total} ${t("prs_toast_smtp_b")}`, { duration: 8000 });
      } else {
        toast.success(`${sent}/${total} ${t("prs_toast_sent_ok")}`);
      }
      loadSends();
    } catch (e) { toast.error(apiErr(e, t("prs_toast_send_fail"))); }
    finally { setSending(false); setConfirm(null); }
  };

  const sendOne = (x) => {
    if (!window.confirm(`${t("prs_confirm_one_a")} ${x.m} (${x.e}) ?`)) return;
    doSend([{ email: x.e, media: x.m }], "individuel");
  };

  const openMass = (scope) => {
    let recipients, label;
    if (scope === "all") { recipients = ALL; label = t("prs_label_all"); }
    else { recipients = list; label = country === "Tous" ? t("prs_label_shown") : `${t("prs_label_region")} « ${regLabel(country)} »`; }
    if (!recipients.length) return toast.error(t("prs_toast_no_recipients"));
    setConfirm({ label, recipients: recipients.map((x) => ({ email: x.e, media: x.m })), scope, step: 1 });
  };

  // ---- mailto (repli) ----
  const mailtoOne = (x) => window.open(`mailto:${encodeURIComponent(x.e)}?subject=${encodeURIComponent(personalize(active.subject, x.m))}&body=${encodeURIComponent(personalize(active.body, x.m))}`, "_blank");

  const copy = (text, label) => navigator.clipboard.writeText(text).then(() => toast.success(`${label} ${t("prs_toast_copied")}`)).catch(() => toast.error(t("prs_toast_copy_fail")));
  const copyAllEmails = () => copy(list.map((x) => x.e).join(", "), `${list.length} e-mails`);
  const exportCsv = () => {
    const rows = [["Média", "Pays", "Type", "Email", "Site"], ...list.map((x) => [x.m, x.c, x.t, x.e, x.w])];
    const csv = rows.map((r) => r.map((v) => `"${v || ""}"`).join(",")).join("\n");
    download("contacts-presse-aca.csv", csv, "text/csv");
  };

  const statusBadge = (x) => {
    const s = sendMap[x.e];
    if (!s) return <span className="text-[11px] text-ink-tertiary">—</span>;
    if (s.opened_at) return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-emerald-400" data-testid={`press-status-${x.e}`}><MailOpen size={11} /> {t("prs_status_opened")} ({s.opens})</span>;
    if (s.status === "envoye") return <span className="rounded-full bg-cyan/15 px-2 py-0.5 text-[10px] font-600 uppercase text-cyan" data-testid={`press-status-${x.e}`}>{t("prs_status_sent")}</span>;
    if (s.status === "non_configure") return <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-amber-400" data-testid={`press-status-${x.e}`}>{t("prs_status_notsent")}</span>;
    return <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-red-400" data-testid={`press-status-${x.e}`}>{t("prs_status_fail")}</span>;
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("prs_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white sm:text-4xl">{t("prs_title")}</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-secondary">
        {t("prs_intro")}
      </p>

      {/* Onglets encadrés et colorés des actions presse (clic = ouvrir, reclic = fermer) */}

      {/* Section « Statistiques » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-statistiques">Statistiques</div>
      {/* Ambassadeurs presse : un code unique par acteur presse */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan/25 bg-cyan/5 p-4" data-testid="press-ambassadors-banner">
        <div className="flex items-start gap-3">
          <Award size={18} className="mt-0.5 text-cyan" />
          <div>
            <div className="font-display text-sm font-700 text-white">Ambassadeurs presse</div>
            <p className="mt-0.5 text-xs leading-relaxed text-ink-secondary">
              Générez un code d&apos;invitation unique pour chaque acteur presse afin de suivre sa réaction :
              ouverture du lien, connexion, pages consultées et analyse complète (onglet Ambassadeurs).
            </p>
          </div>
        </div>
        <button onClick={pressToAmbassadors} disabled={importing} data-testid="press-to-ambassadors"
          className="aca-btn-primary inline-flex shrink-0 items-center gap-2 disabled:opacity-50">
          <Award size={14} /> {importing ? "Création…" : "Créer un code par acteur presse"}
        </button>
      </div>

      {/* Analyse / suivi de réception */}
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="press-analytics">
        {[
          { k: "sent", label: t("prs_stat_sent"), v: analytics?.sent ?? 0 },
          { k: "opened", label: t("prs_stat_opened"), v: analytics?.opened ?? 0 },
          { k: "rate", label: t("prs_stat_rate"), v: `${analytics?.open_rate ?? 0} %` },
          { k: "opens", label: t("prs_stat_opens"), v: analytics?.total_opens ?? 0 },
        ].map((c) => (
          <div key={c.k} className="border border-white/10 bg-surface p-4" data-testid={`press-stat-${c.k}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-tertiary"><BarChart3 size={12} className="text-cyan" /> {c.label}</div>
            <div className="mt-2 font-display text-2xl font-900 text-white">{c.v}</div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-ink-tertiary">
        {t("prs_tracking_note")}
      </p>

      {/* Traçabilité des clics — total + par média */}
      <div className="mt-6 rounded-xl border border-cyan/25 bg-cyan/5 p-5" data-testid="press-tracking">
        <div className="mb-3 flex items-center gap-2 text-sm font-700 text-white"><BarChart3 size={16} className="text-cyan" /> Traçabilité des clics — un code unique par média</div>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-white/10 bg-void p-4" data-testid="press-track-clicks">
            <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">Clics totaux (ouvertures de lien)</div>
            <div className="mt-1 font-display text-2xl font-900 text-white">{tracking?.totals?.clicks ?? 0}</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-void p-4" data-testid="press-track-pages">
            <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">Pages visitées (total)</div>
            <div className="mt-1 font-display text-2xl font-900 text-white">{tracking?.totals?.pages ?? 0}</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-void p-4" data-testid="press-track-medias">
            <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">Médias tracés (codes)</div>
            <div className="mt-1 font-display text-2xl font-900 text-white">{tracking?.totals?.medias ?? 0}</div>
          </div>
        </div>
        <p className="mt-2 text-xs text-ink-tertiary">Le détail par média (clics locaux + pages) est indiqué dans la colonne « {t("prs_th_tracking")} » du tableau ci-dessous.</p>
      </div>
      </>

      {/* Section « Campagne e-mail » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-campagne">Campagne e-mail</div>
      {/* Email de présentation */}
      <div className="mt-8 border border-cyan/30 bg-surface p-6" data-testid="press-email">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-display text-lg font-700 text-white"><Newspaper size={18} className="text-cyan" /> {t("prs_email_title")}</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => copy(active.subject, t("prs_subject"))} data-testid="press-copy-subject" className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-white transition-colors hover:border-cyan"><Copy size={13} /> {t("prs_copy_subject")}</button>
            <button onClick={() => copy(personalize(active.body, t("prs_media_ph")), t("prs_message"))} data-testid="press-copy-body" className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-white transition-colors hover:border-cyan"><Copy size={13} /> {t("prs_copy_body")}</button>
          </div>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("prs_template")}</span>
          {Object.keys(allTemplates).map((key) => (
            <button key={key} onClick={() => setTpl(key)} data-testid={`press-tpl-${key}`}
              className={`rounded-full border px-3 py-1.5 text-xs uppercase tracking-wide transition-all ${tpl === key ? "border-cyan bg-cyan text-void" : "border-white/15 text-ink-secondary hover:text-white"}`}>{tplLabel(key)}</button>
          ))}
          {active.custom ? (
            <>
              <button onClick={() => setTplModal({ mode: "edit", id: active.id, label: active.label, subject: active.subject, body: active.body })} data-testid="press-tpl-edit" className="aca-btn-secondary inline-flex items-center gap-1 text-white"><FileText size={12} /> Modifier</button>
              <button onClick={() => deleteTemplate(active.id)} data-testid="press-tpl-delete" className="inline-flex items-center gap-1 rounded-full border border-red-400/40 px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-red-400/10"><Trash2 size={12} /> Supprimer</button>
            </>
          ) : (
            <>
              <button onClick={() => setTplModal({ builtin: tpl, subject: active.subject, body: active.body })} data-testid="press-tpl-edit-builtin" className="aca-btn-secondary inline-flex items-center gap-1 text-white"><FileText size={12} /> Modifier</button>
              {builtinTpls[tpl]?.subject && (
                <button onClick={() => resetBuiltin(tpl)} data-testid="press-tpl-reset" className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs text-ink-secondary transition-colors hover:text-white"><RotateCcw size={12} /> Réinitialiser</button>
              )}
            </>
          )}
          <button onClick={() => setTplModal({ mode: "add", label: "", subject: active.subject, body: active.body })} data-testid="press-tpl-add" className="inline-flex items-center gap-1 rounded-full border border-cyan/50 px-3 py-1.5 text-xs font-600 text-cyan transition-colors hover:bg-cyan/10"><Plus size={12} /> Ajouter un modèle</button>
        </div>
        <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("prs_subject")}</div>
        <div className="mb-4 border border-white/10 bg-void px-4 py-3 text-sm text-white">{active.subject}</div>
        <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("prs_message")}</div>
        <pre className="whitespace-pre-wrap border border-white/10 bg-void px-4 py-4 text-sm leading-relaxed text-ink-secondary" style={{ fontFamily: "inherit" }}>{personalize(active.body, t("prs_media_ph"))}</pre>
        <p className="mt-2 text-xs text-ink-tertiary"><span className="text-cyan">{t("prs_media_ph")}</span> {t("prs_media_note_a")}</p>
      </div>

      {/* Kit presse */}
      <div className="mt-8 border border-white/10 bg-surface p-5" data-testid="press-kit">
        <div className="mb-4 flex items-center gap-2 text-sm font-600 text-white"><FileText size={16} className="text-cyan" /> {t("prs_kit_title")}</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => download("communique-presse-aca.txt", `${active.subject}\n\n${personalize(active.body, t("prs_media_ph"))}`)} data-testid="press-kit-release" className="aca-btn-secondary inline-flex items-center gap-1.5 text-white"><FileText size={14} /> {t("prs_kit_release")}</button>
          <a href="/press-banner-aca.jpg" download data-testid="press-kit-banner" className="aca-btn-secondary inline-flex items-center gap-1.5 text-white"><ImageIcon size={14} /> {t("prs_kit_banner")}</a>
          <a href="/aca-logo-white.png" download data-testid="press-kit-logo" className="aca-btn-secondary inline-flex items-center gap-1.5 text-white"><Download size={14} /> {t("prs_kit_logo")}</a>
          <a href="/app-icon.png" download data-testid="press-kit-icon" className="aca-btn-secondary inline-flex items-center gap-1.5 text-white"><Download size={14} /> {t("prs_kit_icon")}</a>
        </div>
      </div>
      </>

      {/* Section « Contacts » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-contacts">Contacts</div>
      {/* Ajouter un contact presse non enregistré */}
      <div className="mt-8 border border-white/10 bg-surface p-5" data-testid="press-add-form">
        <div className="mb-4 flex items-center gap-2 text-sm font-600 text-white"><Plus size={16} className="text-cyan" /> {t("prs_add_title")}</div>
        <p className="-mt-2 mb-4 flex items-center gap-1.5 text-xs text-cyan"><Award size={13} /> Règle automatique : chaque nouveau média est aussitôt intégré aux Ambassadeurs (un code de suivi est créé).</p>
        <div className="grid gap-3 sm:grid-cols-4">
          <input value={nc.m} onChange={(e) => setNc({ ...nc, m: e.target.value })} placeholder={t("prs_new_media")} data-testid="press-new-media" className="border border-white/10 bg-void px-3 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
          <input value={nc.e} onChange={(e) => setNc({ ...nc, e: e.target.value })} placeholder={t("prs_new_email")} data-testid="press-new-email" className="border border-white/10 bg-void px-3 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
          <input value={nc.t} onChange={(e) => setNc({ ...nc, t: e.target.value })} placeholder={t("prs_new_type")} data-testid="press-new-type" className="border border-white/10 bg-void px-3 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
          <input value={nc.c} onChange={(e) => setNc({ ...nc, c: e.target.value })} placeholder={t("prs_new_country")} data-testid="press-new-country" className="border border-white/10 bg-void px-3 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
        </div>
        <button onClick={addCustom} data-testid="press-add-btn" className="aca-btn-primary mt-3 inline-flex items-center gap-1.5"><Plus size={14} /> {t("prs_add_btn")}</button>
      </div>

      {/* Filtres + envoi en masse */}
      <div className="mt-10 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {REGION_KEYS.map((c) => (
            <button key={c} onClick={() => setCountry(c)} data-testid={`press-country-${slug(c)}`}
              className={`border px-4 py-2 text-xs uppercase tracking-wider transition-all hover:-translate-y-0.5 ${country === c ? "border-cyan bg-cyan text-void" : "border-white/15 text-ink-secondary hover:text-white"}`}>{regLabel(c)}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("prs_search")} data-testid="press-search"
            className="w-full border border-white/10 bg-surface py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
        </div>
        <button onClick={copyAllEmails} data-testid="press-copy-all" className="inline-flex items-center gap-1.5 border border-white/15 px-4 py-2.5 text-xs text-white transition-colors hover:border-cyan"><Mail size={14} /> {t("prs_copy_all")}</button>
        <button onClick={exportCsv} data-testid="press-export" className="inline-flex items-center gap-1.5 border border-white/15 px-4 py-2.5 text-xs text-white transition-colors hover:border-cyan"><Download size={14} /> {t("prs_export")}</button>
        <button onClick={disableRegion} data-testid="press-disable-region" className="inline-flex items-center gap-1.5 border border-red-400/40 px-4 py-2.5 text-xs text-red-400 transition-colors hover:bg-red-400/10">
          <Trash2 size={14} /> {country === "Tous" ? "Désactiver la sélection" : `Désactiver « ${regLabel(country)} »`}
        </button>
        {hidden.length > 0 && (
          <button onClick={restoreAll} data-testid="press-restore" className="inline-flex items-center gap-1.5 border border-white/15 px-4 py-2.5 text-xs text-ink-secondary transition-colors hover:text-white">
            {hidden.length} masqué(s) · Réafficher tout
          </button>
        )}
      </div>

      {/* Boutons d'envoi de masse */}
      <div className="mt-4 flex flex-wrap items-center gap-3 border border-white/10 bg-surface p-4" data-testid="press-mass-actions">
        <span className="text-xs uppercase tracking-wider text-ink-tertiary">{t("prs_mass_label")}</span>
        <button onClick={() => openMass("region")} data-testid="press-mass-region" className="aca-btn-primary inline-flex items-center gap-1.5 disabled:opacity-50" disabled={sending}>
          <Send size={14} /> {country === "Tous" ? `${t("prs_mass_shown")} (${list.length})` : `${t("prs_mass_region")} ${regLabel(country)} (${list.length})`}
        </button>
        <button onClick={() => openMass("all")} data-testid="press-mass-all" className="inline-flex items-center gap-1.5 rounded-full border border-cyan px-5 py-2.5 text-xs font-600 uppercase tracking-wide text-cyan transition-all hover:bg-cyan hover:text-void disabled:opacity-50" disabled={sending}>
          <Send size={14} /> {t("prs_mass_all")} ({ALL.length})
        </button>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="border-b border-white/10 bg-surface text-[11px] uppercase tracking-wider text-ink-tertiary">
            <tr><th className="p-4">{t("prs_th_media")}</th><th className="p-4">{t("prs_th_country")}</th><th className="p-4">{t("prs_th_type")}</th><th className="p-4">{t("prs_th_email")}</th><th className="p-4">{t("prs_th_tracking")}</th><th className="p-4">{t("prs_th_action")}</th></tr>
          </thead>
          <tbody>
            {list.map((x, i) => (
              <tr key={x.e + i} className="border-b border-white/5 hover:bg-surface" data-testid={`press-row-${i}`}>
                <td className="p-4 font-600 text-white">
                  <div className="flex items-center gap-2">
                    {x.m}
                    {x.custom && <span className="rounded-full bg-cyan/15 px-2 py-0.5 text-[10px] uppercase text-cyan">{t("prs_added_badge")}</span>}
                    {hasNote(x.e) && <span title="Fiche annotée" className="h-1.5 w-1.5 rounded-full bg-amber-400" data-testid={`press-note-dot-${i}`} />}
                  </div>
                  {x.p ? <div className="mt-1 flex items-center gap-0.5" title={`Priorité ${x.p}/5`}>{Array.from({ length: x.p }).map((_, k) => <Star key={k} size={10} className="fill-amber-400 text-amber-400" />)}</div> : null}
                </td>
                <td className="p-4 text-ink-secondary">{x.c}</td>
                <td className="p-4 text-ink-secondary">{x.t}</td>
                <td className="p-4">
                  <button onClick={() => copy(x.e, t("prs_th_email"))} className="inline-flex items-center gap-1.5 text-cyan hover:underline">{x.e}</button>
                </td>
                <td className="p-4">{statusBadge(x)}{sendMap[x.e]?.last_open_at && <div className="mt-1 text-[10px] text-ink-tertiary">{fmtDate(sendMap[x.e].last_open_at)}</div>}
                  {trk(x.e) && (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]" data-testid={`press-track-row-${i}`}>
                      <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-cyan">{trk(x.e).code}</span>
                      <span className="text-ink-tertiary">{trk(x.e).clicks} clic(s) · {trk(x.e).pages} page(s)</span>
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => sendOne(x)} data-testid={`press-send-${i}`} disabled={sending} className="aca-btn-primary inline-flex items-center gap-1 disabled:opacity-50"><Send size={12} /> {t("prs_send")}</button>
                    <button onClick={() => setNoteModal({ x, ...(getNote(x.e) || { particularites: "", note: "" }) })} data-testid={`press-note-${i}`} title="Notes & particularités" className={`transition-colors hover:text-amber-400 ${hasNote(x.e) ? "text-amber-400" : "text-ink-tertiary"}`}><StickyNote size={15} /></button>
                    <button onClick={() => mailtoOne(x)} data-testid={`press-mailto-${i}`} title={t("prs_open_mail")} className="text-ink-tertiary transition-colors hover:text-cyan"><Mail size={15} /></button>
                    <button onClick={() => deleteRow(x)} data-testid={`press-del-${i}`} title="Désactiver / masquer ce contact" className="text-ink-tertiary hover:text-red-400"><EyeOff size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-ink-tertiary">
        {t("prs_disclaimer")}
      </p>

      {/* Espace « Désactivés » — réactivation par contact ou par pays */}
      <div className="mt-8 rounded-xl border border-white/10 bg-surface" data-testid="press-disabled-section">
        <button onClick={() => setShowHidden(!showHidden)} data-testid="press-disabled-toggle" className="flex w-full items-center justify-between px-5 py-4 text-left">
          <div className="flex items-center gap-2">
            <EyeOff size={16} className="text-amber-400" />
            <span className="font-display text-base font-700 text-white">Contacts désactivés</span>
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-600 text-amber-400">{hiddenList.length}</span>
          </div>
          <div className="flex items-center gap-3">
            {hiddenList.length > 0 && (
              <span onClick={(e) => { e.stopPropagation(); restoreAll(); }} data-testid="press-restore-all" className="cursor-pointer rounded-full border border-cyan/40 px-3 py-1.5 text-[11px] uppercase tracking-wide text-cyan transition-colors hover:bg-cyan/10">Tout réactiver</span>
            )}
            <span className="text-xs text-ink-tertiary">{showHidden ? "Masquer" : "Afficher"}</span>
          </div>
        </button>

        {showHidden && (
          <div className="border-t border-white/10 p-5">
            {hiddenList.length === 0 ? (
              <p className="text-sm text-ink-tertiary" data-testid="press-disabled-empty">Aucun contact désactivé. Tous les contacts sont actifs.</p>
            ) : (
              <div className="space-y-6">
                {Object.entries(hiddenByCountry).map(([pays, items]) => (
                  <div key={pays} data-testid={`press-disabled-country-${slug(pays)}`}>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{pays} · {items.length}</div>
                      <button onClick={() => unhideContacts(items.map((x) => x.e), `${items.length} contact(s) de « ${pays} » réactivé(s).`)} data-testid={`press-reactivate-country-${slug(pays)}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-cyan/40 px-3 py-1.5 text-[11px] uppercase tracking-wide text-cyan transition-colors hover:bg-cyan/10">
                        <RotateCcw size={12} /> Réactiver ce pays
                      </button>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((x, i) => (
                        <div key={x.e + i} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-void px-3 py-2 opacity-80" data-testid={`press-disabled-row-${slug(x.e)}`}>
                          <div className="min-w-0">
                            <div className="truncate text-sm text-white">{x.m}</div>
                            <div className="truncate text-[11px] text-ink-tertiary">{x.e}</div>
                          </div>
                          <button onClick={() => unhideContacts([x.e], `« ${x.m} » réactivé.`)} data-testid={`press-reactivate-${slug(x.e)}`}
                            className="aca-btn-primary inline-flex shrink-0 items-center gap-1">
                            <RotateCcw size={12} /> Réactiver
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </>

      {/* Modale Notes & particularités */}
      {noteModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="press-note-modal">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2 font-display text-lg font-700 text-white"><StickyNote size={18} className="text-amber-400" /> {noteModal.x.m}</div>
              <button onClick={() => setNoteModal(null)} data-testid="press-note-close" className="text-ink-tertiary hover:text-white"><X size={20} /></button>
            </div>
            <div className="mb-5 text-xs text-ink-tertiary">{noteModal.x.e}</div>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Particularités (angle, journaliste, rubrique…)</span>
              <input value={noteModal.particularites} onChange={(e) => setNoteModal({ ...noteModal, particularites: e.target.value })} data-testid="press-note-particularites"
                className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
            </label>
            <label className="mt-4 block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Notes libres sur cette société de presse</span>
              <textarea rows={6} value={noteModal.note} onChange={(e) => setNoteModal({ ...noteModal, note: e.target.value })} data-testid="press-note-text"
                className="w-full resize-none rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setNoteModal(null)} className="aca-btn-secondary text-white">Annuler</button>
              <button onClick={saveNote} data-testid="press-note-save" className="aca-btn-primary">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale d'édition / ajout de modèle d'e-mail */}
      {tplModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="press-tpl-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2 font-display text-lg font-700 text-white"><Newspaper size={18} className="text-cyan" /> {tplModal.builtin ? `Modifier le modèle ${tplModal.builtin === "long" ? "« Complet »" : "« Court »"}` : tplModal.mode === "edit" ? "Modifier le modèle" : "Nouveau modèle d'e-mail"}</div>
              <button onClick={() => setTplModal(null)} data-testid="press-tpl-modal-close" className="text-ink-tertiary hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {!tplModal.builtin && (
              <label className="block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Intitulé du modèle</span>
                <input value={tplModal.label} onChange={(e) => setTplModal({ ...tplModal, label: e.target.value })} data-testid="press-tpl-label" placeholder="Ex. Relance lancement, Version people…"
                  className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
              </label>
              )}
              <label className="block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Objet de l&apos;e-mail</span>
                <input value={tplModal.subject} onChange={(e) => setTplModal({ ...tplModal, subject: e.target.value })} data-testid="press-tpl-subject"
                  className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Message</span>
                <textarea rows={12} value={tplModal.body} onChange={(e) => setTplModal({ ...tplModal, body: e.target.value })} data-testid="press-tpl-body"
                  className="w-full resize-none rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm leading-relaxed text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" style={{ fontFamily: "inherit" }} />
              </label>
              <p className="text-xs text-ink-tertiary">Astuce : utilisez <span className="text-cyan">[MEDIA]</span> pour insérer automatiquement le nom du média du destinataire.</p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setTplModal(null)} className="aca-btn-secondary text-white">Annuler</button>
              <button onClick={saveTemplate} disabled={savingTpl} data-testid="press-tpl-save" className="aca-btn-primary disabled:opacity-50">{savingTpl ? "Enregistrement…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de double confirmation */}      {confirm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="press-confirm-modal">
          <div className="w-full max-w-md border border-cyan/30 bg-surface p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 font-display text-lg font-700 text-white"><AlertTriangle size={18} className="text-amber-400" /> {confirm.step === 1 ? t("prs_cf_title1") : t("prs_cf_title2")}</div>
              <button onClick={() => setConfirm(null)} data-testid="press-confirm-close" className="text-ink-tertiary hover:text-white"><X size={18} /></button>
            </div>
            {confirm.step === 1 ? (
              <>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  {t("prs_cf_intro")} <span className="font-700 text-white">{confirm.recipients.length} {t("prs_cf_recipients")}</span> ({confirm.label}).
                </p>
                <p className="mt-2 text-xs text-ink-tertiary">{t("prs_cf_subject")} « {active.subject} » · {t("prs_cf_model")} {tplLabel(tpl)}</p>
                <div className="mt-6 flex justify-end gap-3">
                  <button onClick={() => setConfirm(null)} data-testid="press-confirm-cancel" className="aca-btn-secondary text-white">{t("prs_cancel")}</button>
                  <button onClick={() => setConfirm({ ...confirm, step: 2 })} data-testid="press-confirm-step1" className="aca-btn-primary">{t("prs_continue")}</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  {t("prs_cf_final_a")} <span className="font-700 text-white">{confirm.recipients.length}</span> {t("prs_cf_final_b")}
                </p>
                <div className="mt-6 flex justify-end gap-3">
                  <button onClick={() => setConfirm({ ...confirm, step: 1 })} data-testid="press-confirm-back" className="aca-btn-secondary text-white">{t("prs_back")}</button>
                  <button onClick={() => doSend(confirm.recipients, confirm.scope)} disabled={sending} data-testid="press-confirm-send" className="aca-btn-primary disabled:opacity-50">{sending ? t("prs_sending") : t("prs_send_final")}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
